import { apiRequest } from "./client";
import {
  LiveResponse, PrematchResponse, OverviewResponse, TodayPerformanceResponse, FeedResponse, LiveDetailResponse,
  LiveNowFixture, LiveCenterFixture,
} from "@/types/api";
import { normalizePrematch } from "./prematchNormalizer";
import { normalizeLiveNow, normalizeLiveCenter } from "./liveNormalizer";

// ---- LIVE (limit must be >= 10 upstream) ----
// Auth-aware: when a session token is present the upstream enriches each signal
// row with the CURRENT live overlay (live_minute / live_score_* / live_status)
// and real crests/ids; unauthenticated/preview callers get the public snapshot
// (signal minute + initial_* only, no crests). The token is attached only when
// present, so preview sessions are unaffected.
export function fetchLive(limit = 100): Promise<LiveResponse> {
  return apiRequest<LiveResponse>(`/live?limit=${Math.max(10, limit)}`, { auth: true, retries: 2 });
}

// ---- Prematch ----
// Both the public and authenticated (V2) surfaces are mapped through the shared
// normalizer so the UI consumes one PrematchResponse view model. The public
// surface is already grouped; V2 is an ATLAS-curated selection envelope.
export async function fetchPrematch(limit = 100): Promise<PrematchResponse> {
  const raw = await apiRequest<any>(`/prematch?grouped=true&limit=${Math.max(10, limit)}`, { retries: 2 });
  return normalizePrematch(raw);
}
// Authenticated ATLAS Prematch V2 — the CURRENT upcoming discovery source.
// Bridge /atlas/prematch-v2 -> upstream /app/api/atlas-prematch-v2. The customer
// /api/prematch route is historical (resolved candidates, id DESC) and must NOT
// be used for discovery. Logo/id enrichment on V2 will be added server-side
// later; until then the list may fall back to initials — correct upcoming
// discovery takes priority over logo enrichment.
export async function fetchPrematchV2(limit = 100): Promise<PrematchResponse> {
  const raw = await apiRequest<any>(`/prematch-v2?grouped=true&limit=${Math.max(10, limit)}`, { auth: true, retries: 1 });
  return normalizePrematch(raw);
}

// ---- Intelligence overview / performance / feed ----
export function fetchOverview(): Promise<OverviewResponse> {
  return apiRequest<OverviewResponse>(`/overview`, { retries: 2 });
}
export function fetchTodayPerformance(): Promise<TodayPerformanceResponse> {
  return apiRequest<TodayPerformanceResponse>(`/today-performance`, { retries: 2 });
}
export function fetchFeed(limit = 12): Promise<FeedResponse> {
  return apiRequest<FeedResponse>(`/feed?limit=${limit}`, { retries: 2 });
}
export function fetchActiveSignals(limit = 12): Promise<LiveResponse> {
  return apiRequest<LiveResponse>(`/active-signals?limit=${limit}`, { retries: 2 });
}

// Rich per-decision ATLAS detail (reasoning + replay timeline + outcome).
export function fetchLiveDetail(decisionId: string): Promise<LiveDetailResponse> {
  return apiRequest<LiveDetailResponse>(`/live-detail/${decisionId}`, { retries: 1 });
}

// Authoritative realtime LIVE NOW feed (authenticated; global via ?live=all).
export async function fetchLiveNow(): Promise<LiveNowFixture[]> {
  const raw = await apiRequest<any>(`/data/live?live=all`, { auth: true, retries: 1 });
  return normalizeLiveNow(raw);
}

// Generic per-fixture Live Center detail (authenticated). Preserves fixture_id.
export async function fetchLiveCenter(fixtureId: string): Promise<LiveCenterFixture | null> {
  const raw = await apiRequest<any>(`/data/live-center/${fixtureId}`, { auth: true, retries: 0 });
  return normalizeLiveCenter(raw);
}

// ---- ATLAS assistant (requires Bearer token upstream) ----
export interface AssistantReply {
  reply?: string;
  message?: string;
  text?: string;
  [k: string]: unknown;
}
export function sendAtlasMessage(message: string, context?: Record<string, unknown>): Promise<AssistantReply> {
  // Upstream /coach-atlas/message requires a per-request Idempotency-Key header.
  const idem = (globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`);
  return apiRequest<AssistantReply>(`/assistant`, {
    method: "POST",
    body: { message, ...(context || {}) },
    auth: true,
    headers: { "Idempotency-Key": idem },
  });
}
